import { API_URL } from '../config';
import { useBranding, logoHeader } from '../context/BrandingContext';
import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Send } from 'lucide-react';
import axios from 'axios';

const NPSPesquisa = () => {
  const { token } = useParams();
  const { branding } = useBranding();

  const [searchParams] = useSearchParams();
  const notaParam = searchParams.get('nota');

  const [loading, setLoading] = useState(true);
  const [pesquisa, setPesquisa] = useState(null);
  const [nota, setNota] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [etapa, setEtapa] = useState('votar'); // 'votar', 'feedback', 'obrigado'
  const [erro, setErro] = useState(null);


  useEffect(() => {
    carregarPesquisa();
  }, [token]);

  useEffect(() => {
    // Se veio nota na URL, registra automaticamente
    if (notaParam && pesquisa && !pesquisa.respondido) {
      const notaNum = parseInt(notaParam);
      if (notaNum >= 0 && notaNum <= 10) {
        registrarVoto(notaNum);
      }
    }
  }, [notaParam, pesquisa]);

  const carregarPesquisa = async () => {
    try {
      const response = await axios.get(`${API_URL}/nps/v/${token}`);
      setPesquisa(response.data);
      if (response.data.respondido) {
        setNota(response.data.nota);
        setEtapa('feedback');
      }
    } catch (error) {
      setErro(error.response?.data?.detail || 'Pesquisa nao encontrada');
    } finally {
      setLoading(false);
    }
  };

  const registrarVoto = async (notaSelecionada) => {
    setEnviando(true);
    try {
      await axios.post(`${API_URL}/nps/votar`, {
        token: token,
        nota: notaSelecionada
      });
      setNota(notaSelecionada);
      setEtapa('feedback');
    } catch (error) {
      setErro(error.response?.data?.detail || 'Erro ao registrar voto');
    } finally {
      setEnviando(false);
    }
  };

  const enviarFeedback = async () => {
    if (!feedback.trim()) {
      // Feedback e opcional, pode pular
      setEtapa('obrigado');
      return;
    }

    setEnviando(true);
    try {
      await axios.post(`${API_URL}/nps/feedback`, {
        token: token,
        feedback: feedback
      });
      setEtapa('obrigado');
    } catch (error) {
      // Mesmo com erro, mostra obrigado
      setEtapa('obrigado');
    } finally {
      setEnviando(false);
    }
  };

  const getCorNota = (n) => {
    if (n <= 6) return '#e74c3c'; // Detratores - vermelho
    if (n <= 8) return '#f39c12'; // Passivos - amarelo
    return '#27ae60'; // Promotores - verde
  };

  const getCategoriaNota = () => {
    if (nota === null) return '';
    if (nota <= 6) return { texto: 'Lamentamos que nao tenha tido uma boa experiencia', emoji: '' };
    if (nota <= 8) return { texto: 'Obrigado pelo seu feedback!', emoji: '' };
    return { texto: 'Ficamos muito felizes com sua avaliacao!', emoji: '' };
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.loading}>Carregando...</div>
        </div>
      </div>
    );
  }

  if (erro) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <AlertCircle size={48} color="#e74c3c" />
          <h2 style={styles.erroTitulo}>Ops!</h2>
          <p style={styles.erroTexto}>{erro}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <img
            src={logoHeader(branding)}
            alt={branding.nome_operadora}
            style={styles.logo}
          />
        </div>

        {/* Etapa 1: Votar */}
        {etapa === 'votar' && (
          <>
            <h1 style={styles.titulo}>Como foi sua experiencia?</h1>
            <p style={styles.subtitulo}>
              Em uma escala de 0 a 10, o quanto voce recomendaria a {branding.nome_operadora} para um amigo ou colega?
            </p>

            <div style={styles.notasContainer}>
              {/* Detratores */}
              <div style={styles.linhaNotas}>
                {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                  <button
                    key={n}
                    onClick={() => registrarVoto(n)}
                    disabled={enviando}
                    style={{
                      ...styles.botaoNota,
                      backgroundColor: getCorNota(n),
                      opacity: enviando ? 0.5 : 1
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {/* Passivos */}
              <div style={styles.linhaNotas}>
                {[7, 8].map((n) => (
                  <button
                    key={n}
                    onClick={() => registrarVoto(n)}
                    disabled={enviando}
                    style={{
                      ...styles.botaoNota,
                      backgroundColor: getCorNota(n),
                      opacity: enviando ? 0.5 : 1
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {/* Promotores */}
              <div style={styles.linhaNotas}>
                {[9, 10].map((n) => (
                  <button
                    key={n}
                    onClick={() => registrarVoto(n)}
                    disabled={enviando}
                    style={{
                      ...styles.botaoNota,
                      backgroundColor: getCorNota(n),
                      opacity: enviando ? 0.5 : 1
                    }}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.legenda}>
              <span style={{ color: '#e74c3c' }}>0-6: Nao recomendaria</span>
              <span style={{ color: '#f39c12' }}>7-8: Neutro</span>
              <span style={{ color: '#27ae60' }}>9-10: Recomendaria muito</span>
            </div>
          </>
        )}

        {/* Etapa 2: Feedback */}
        {etapa === 'feedback' && (
          <>
            <div style={styles.notaSelecionada}>
              <div style={{ ...styles.circuloNota, backgroundColor: getCorNota(nota) }}>
                {nota}
              </div>
            </div>

            <h2 style={styles.tituloFeedback}>{getCategoriaNota().texto}</h2>

            <p style={styles.subtitulo}>
              (Opcional) Nos conte mais sobre o motivo da sua nota:
            </p>

            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Ex: O atendimento foi rapido, mas..."
              style={styles.textarea}
              rows={4}
            />

            <button
              onClick={enviarFeedback}
              disabled={enviando}
              style={styles.botaoEnviar}
            >
              <Send size={18} />
              {enviando ? 'Enviando...' : 'Enviar'}
            </button>

            <button
              onClick={() => setEtapa('obrigado')}
              style={styles.botaoPular}
            >
              Pular
            </button>
          </>
        )}

        {/* Etapa 3: Obrigado */}
        {etapa === 'obrigado' && (
          <>
            <CheckCircle size={64} color="var(--brand, #20a490)" />
            <h2 style={styles.tituloObrigado}>Muito obrigado!</h2>
            <p style={styles.textoObrigado}>
              Sua opiniao e muito importante para continuarmos melhorando nossos servicos.
            </p>
            <p style={styles.protocolo}>
              Protocolo: {pesquisa?.protocolo}
            </p>
          </>
        )}

        {/* Rodape */}
        <div style={styles.rodape}>
          <p style={styles.rodapeTexto}>{branding.nome_operadora}{branding.tagline ? ` - ${branding.tagline}` : ''}</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center',
  },
  logoContainer: {
    marginBottom: '30px',
  },
  logo: {
    maxWidth: '180px',
    height: 'auto',
  },
  titulo: {
    fontSize: '24px',
    color: '#333',
    marginBottom: '15px',
    fontWeight: '600',
  },
  subtitulo: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '30px',
    lineHeight: '1.5',
  },
  notasContainer: {
    marginBottom: '20px',
  },
  linhaNotas: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  botaoNota: {
    width: '44px',
    height: '44px',
    borderRadius: '8px',
    border: 'none',
    color: 'white',
    fontSize: '18px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  legenda: {
    display: 'flex',
    justifyContent: 'center',
    gap: '15px',
    fontSize: '12px',
    marginTop: '20px',
  },
  notaSelecionada: {
    marginBottom: '20px',
  },
  circuloNota: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '36px',
    fontWeight: 'bold',
  },
  tituloFeedback: {
    fontSize: '20px',
    color: '#333',
    marginBottom: '20px',
  },
  textarea: {
    width: '100%',
    padding: '15px',
    borderRadius: '8px',
    border: '1px solid #ddd',
    fontSize: '14px',
    resize: 'vertical',
    marginBottom: '20px',
    fontFamily: 'inherit',
  },
  botaoEnviar: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'var(--brand, #20a490)',
    color: 'var(--brand-ink, #fff)',
    border: 'none',
    padding: '12px 30px',
    borderRadius: '8px',
    fontSize: '16px',
    cursor: 'pointer',
    marginBottom: '10px',
  },
  botaoPular: {
    display: 'block',
    background: 'none',
    border: 'none',
    color: '#999',
    fontSize: '14px',
    cursor: 'pointer',
    marginTop: '10px',
  },
  tituloObrigado: {
    fontSize: '28px',
    color: 'var(--brand, #20a490)',
    marginTop: '20px',
    marginBottom: '15px',
  },
  textoObrigado: {
    fontSize: '16px',
    color: '#666',
    lineHeight: '1.5',
  },
  protocolo: {
    fontSize: '14px',
    color: '#999',
    marginTop: '20px',
  },
  rodape: {
    borderTop: '1px solid #eee',
    marginTop: '30px',
    paddingTop: '20px',
  },
  rodapeTexto: {
    color: 'var(--brand, #20a490)',
    fontWeight: '600',
    fontSize: '14px',
  },
  loading: {
    padding: '40px',
    color: '#666',
  },
  erroTitulo: {
    color: '#e74c3c',
    marginTop: '15px',
  },
  erroTexto: {
    color: '#666',
  },
};

export default NPSPesquisa;
